import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";

import { SelectEvent } from "@king-ict/eupisi-ui/components";

import {
  type SchoolCandidateBatchDto,
  useLazyGetSchoolCandidateBatchHistoryQuery,
} from "../../../../queries";
import { type SchoolCandidateBatchStatus } from "../../../../models";
import {
  generateComboKey,
  generateInitialBatchStatusData,
  generateSelectData,
} from "./utils";

interface IProps {
  countyId: number | undefined;
  schoolYearId: number;
  batchId: number | null;
  initialBatchStatus: SchoolCandidateBatchStatus | undefined;
  onChange: (batch: SchoolCandidateBatchDto | null) => void;
}

export const useBatchHistory = ({
  countyId,
  schoolYearId,
  batchId,
  initialBatchStatus,
  onChange,
}: IProps) => {
  const [cachedBatchHistory, setCachedBatchHistory] = useState<
    Record<string, SchoolCandidateBatchDto[]>
  >({});

  const [
    fetchBatchHistory,
    {
      data: batchHistoryData,
      isFetching: isBatchHistoryFetching,
      originalArgs,
    },
  ] = useLazyGetSchoolCandidateBatchHistoryQuery();

  const lastSyncedBatchId = useRef<number | null>(null);
  const lastSyncedComboKey = useRef<string | null>(null);

  useEffect(() => {
    if (!batchHistoryData || !originalArgs) {
      return;
    }

    const comboKey = generateComboKey(
      originalArgs.countyId,
      originalArgs.schoolYearId
    );

    setCachedBatchHistory((prev) => ({
      ...prev,
      [comboKey]: batchHistoryData,
    }));
  }, [batchHistoryData, originalArgs]);

  const initialBatchStatusData = useMemo<SchoolCandidateBatchDto>(
    () => generateInitialBatchStatusData(initialBatchStatus),
    [
      initialBatchStatus?.batchId,
      initialBatchStatus?.processedAt,
      initialBatchStatus?.schoolYearId,
    ]
  );

  const comboKey = (() => {
    if (!countyId || !schoolYearId) {
      return null;
    }

    return generateComboKey(countyId, schoolYearId);
  })();

  const hasInitialBatch = !!initialBatchStatus?.batchId;

  const optionsForCombo = useMemo(() => {
    if (!comboKey) {
      return batchHistoryData;
    }

    return cachedBatchHistory[comboKey] ?? batchHistoryData;
  }, [batchHistoryData, cachedBatchHistory, comboKey]);

  const selectData = useMemo(() => {
    if (!countyId || !schoolYearId) {
      return generateSelectData();
    }

    const fallbackInitialOption =
      hasInitialBatch && !batchHistoryData?.length
        ? [initialBatchStatusData]
        : [];

    const options = optionsForCombo ?? fallbackInitialOption;

    const optionsForSelectedSchoolYear =
      options?.filter((batch) => batch.schoolYearId === schoolYearId) ?? [];

    return generateSelectData(optionsForSelectedSchoolYear);
  }, [
    batchHistoryData,
    countyId,
    hasInitialBatch,
    initialBatchStatusData,
    optionsForCombo,
    schoolYearId,
  ]);

  const shouldBeDisabled =
    !countyId ||
    !schoolYearId ||
    isBatchHistoryFetching ||
    !hasInitialBatch;

  const handleOpen = useCallback((): void => {
    if (!comboKey) {
      return;
    }

    const isComboCached = !!cachedBatchHistory[comboKey];

    if (isComboCached) {
      return;
    }

    if (countyId == null || schoolYearId == null) {
      return;
    }

    fetchBatchHistory({
      countyId,
      schoolYearId,
    });
  }, [cachedBatchHistory, comboKey, countyId, schoolYearId, fetchBatchHistory]);

  // Preload batch history so the select shows the correct label
  // immediately when a stored batchId exists, without waiting for a click.
  useEffect(() => {
    handleOpen();
  }, [handleOpen]);

  // Initial load of the batch date select
  useEffect(() => {
    if (
      batchHistoryData?.length ||
      !initialBatchStatus ||
      initialBatchStatus.schoolYearId !== schoolYearId ||
      (batchId && batchId !== initialBatchStatusData.id)
    ) {
      return;
    }

    onChange(initialBatchStatusData);
  }, [
    batchHistoryData,
    batchId,
    initialBatchStatus,
    initialBatchStatusData,
    onChange,
    schoolYearId,
  ]);

  useEffect(() => {
    if (!batchId || !comboKey) {
      return;
    }

    if (!optionsForCombo?.length) {
      return;
    }

    if (
      lastSyncedBatchId.current === batchId &&
      lastSyncedComboKey.current === comboKey
    ) {
      return;
    }

    const matchingBatch = optionsForCombo.find((batch) => batch.id === batchId);

    if (!matchingBatch) {
      return;
    }

    lastSyncedBatchId.current = batchId;
    lastSyncedComboKey.current = comboKey;

    onChange(matchingBatch);
  }, [
    batchId,
    comboKey,
    onChange,
    optionsForCombo,
  ]);

  const handleChange = useCallback(
    (event: ChangeEvent<SelectEvent>): void => {
      const selectedBatchId = Number(event.target.value);

      const batch =
        optionsForCombo?.find((item) => item.id === selectedBatchId) ??
        initialBatchStatusData;

      onChange(batch);
    },
    [initialBatchStatusData, onChange, optionsForCombo]
  );

  return {
    selectData,
    isFetching: isBatchHistoryFetching,
    shouldBeDisabled,
    handleOpen,
    handleChange,
  };
};
